import React, { useCallback, useContext } from "react";

import { PickerDate } from "@euroland/calendar-react";
import { Button } from "@euroland/base-ui";

import Label from "../ui/Label";
import NumberInput from "../ui/NumberInput";
import Controller from "../Form/Controller";
import { classNames, formatDateTime, replaceKey } from "../../utils";
import { useCompanySettings, useFormatSetting, useSelectedInstrument } from "../../hooks/useCompanySettings";
import i18n from "../../locale/i18n";
import { VictorSquareCheckbox } from "../ui/Checkbox";
import withAmountInvestedSelection from "../../hocs/withAmountInvestedSelection";
import { useInstrument } from "../../hooks/useInstruments";
import { isUseLatinNumber } from "../../store/utils";
import { SHORT_DATE_FORMAT, TAB } from "../../constant/common";
import useCurrencyName from "../../hooks/useCurrencyName";
import { Dropdown, Option } from "@euroland/dropdown-react";
import { AmountInvestedSelectContext } from "../../context/AmountAndShareSelectContext";

const VictorAmountInvestedSelection = ({
  formState,
  onSubmit,
  enabledDividendOption,
  loading,
  minimumPriceLoading,
  resolveAmountInvestedErrorMessage,
  minStartDate,
  maxStartDate,
  minEndDate,
  maxEndDate,
}) => {
  const { tab } = useContext(AmountInvestedSelectContext);
  const format = useFormatSetting();
  const companySettings = useCompanySettings();
  const [{ selectedInstrument }] = useInstrument();
  const [selectedInstrumentSetting, { formatNumber }] = useSelectedInstrument();
  const getCurrencyNameByCode = useCurrencyName();

  const { setFieldValue, getFieldValue } = formState;
  const dateTimeValue = selectedInstrumentSetting.limitStartingData || selectedInstrument.startingDate;
  const dateTimeFormat = formatDateTime(dateTimeValue, companySettings);
  const handleFormatNumber = useCallback(value => formatNumber(value), [formatNumber]);
  const calendarFormat = format.calendarDate || format.shortDate || SHORT_DATE_FORMAT;
  const isRtl = window.appSettings.isRtl;
  const amountInvestedErrorMessage = resolveAmountInvestedErrorMessage(formState.error?.["amountInvested"]);

  return (
    <>
      <div className="victor-selection__wrapper">
        <div className="victor-selection__row">
          <div className="victor-selection__period-item">
            <Label
              htmlFor="dateOfInvestment"
              className={classNames({ "d-hidden-accessibility": tab !== TAB.AMOUNT_INVESTED })}
            >
              {i18n.translate("selectPeriod")}
            </Label>
            <div className="victor-selection__picker_wrapper">
              <PickerDate
                id="dateOfInvestment"
                dateFormat={calendarFormat}
                className="victor-selection__input"
                value={getFieldValue("startDate")}
                minDate={minStartDate}
                maxDate={maxStartDate}
                onChange={date => setFieldValue("startDate", date)}
                textCancel={i18n.translate("cancel")}
                textApply={i18n.translate("apply")}
              />
              
              <Label className="d-hidden-accessibility" htmlFor="endDateOfInvestment">
                {i18n.translate("endDateOfInvestment")}
              </Label>

              <PickerDate
                id="endDateOfInvestment"
                dateFormat={calendarFormat}
                align="right"
                className={classNames("victor-selection__input", {
                  left: isRtl,
                  right: !isRtl,
                })}
                value={getFieldValue("endDate")}
                minDate={minEndDate}
                maxDate={maxEndDate}
                onChange={date => setFieldValue("endDate", date)}
                textCancel={i18n.translate("cancel")}
                textApply={i18n.translate("apply")}
              />
            </div>
            <p className="victor-selection__data-starting" tabIndex={0}>
              {replaceKey(i18n.translate("noteDataStarting"), [dateTimeFormat])}
            </p>
          </div>
        </div>
        <div
          className="victor-selection__row"
          style={companySettings.currencyEnabled ? undefined : { rowGap: 0 }}
        >
          {/* <div className="victor-selection__period-item"> */}
          {companySettings.currencyEnabled && tab === TAB.AMOUNT_INVESTED && (
            <div className="victor-selection__item">
              <Label>{i18n.translate("selectCurrency")}</Label>
              <Dropdown
                icon={<span className="fs-chevron-down fs-icon"></span>}
                value={getFieldValue("currency")}
                className="victor-selection__input"
                placeholder={i18n.translate("localCurrency")}
                onOptionSelect={value => setFieldValue("currency", value)}
                aria-label={i18n.translate("localCurrency")}
                dropdownMenuLabel="dropdown currency"
              >
                <Option value="">{i18n.translate("localCurrency")}</Option>
                {companySettings.currencies.map(({ code, text }) => (
                  <Option key={code} value={code} aria-label={getCurrencyNameByCode(code)}>
                    {text || code}
                  </Option>
                ))}
              </Dropdown>
            </div>
          )}

          <div className="victor-selection__item">
            <Label htmlFor="amountInvested">{i18n.translate("amountInvestedInput")}</Label>
            <NumberInput
              useLatinNumbers={isUseLatinNumber}
              className={classNames({ error: (loading === false) && (minimumPriceLoading === false) && !!formState.error?.["amountInvested"] }, "victor-selection__input")}
              value={getFieldValue("amountInvested")}
              onChange={value => setFieldValue("amountInvested", value)}
              formatNumber={handleFormatNumber}
              title={i18n.translate("amountInvested")}
              require
              id="amountInvested"
              errorMessage={loading || minimumPriceLoading ? undefined : amountInvestedErrorMessage}
            />
          </div>
          {/* </div> */}
        </div>
        <div className="victor-selection__submit-wrapper">
          {enabledDividendOption && tab === TAB.AMOUNT_INVESTED && (
            <VictorSquareCheckbox
              ariaLabel=""
              className="victor-selection__checkbox"
              selected={getFieldValue("dividendChecked")}
              onSelect={() => setFieldValue("dividendChecked", !getFieldValue("dividendChecked"))}
            />
          )}
          <Button
            active
            loading={loading}
            disabled={minimumPriceLoading}
            className="victor-selection__submit"
            onClick={onSubmit}
            aria-label={i18n.translate("showData")}
          >
            {i18n.translate("showData")}
          </Button>
        </div>
      </div>
    </>
  );
};

export default withAmountInvestedSelection(VictorAmountInvestedSelection);
